// Text width measurement for timeline label geometry.
//
// The spine labels ("Generating Forecast", "Branch 2") sit in a GAP cut out of the line, and
// the gap's width is computed at render time from the label's width. That width used to be
// guessed — `text.length * (fontSize * 0.58)` and friends — and every guess has to err WIDE,
// because erring narrow runs the dashes through the glyphs. So the error was deliberately
// one-directional and it all landed in the gap: the awaiting spine's label reserved ~154px
// for text that rendered ~107px, and the leftover 47px opened as ~24px of dead line on each
// side, before the 8px fade even started. The gap read as a hole with a caption in it.
//
// A guess also cannot be tuned safely, because the error depends on the FONT: the same
// constant that leaves a hole under the fallback face would let the line touch the type once
// IBM Plex Mono loads. Measuring removes the trade-off — the gap becomes exactly the padding
// the caller asks for, under any face, at any zoom (these are screen px; only the label's
// position moves with the timeline).
//
// Canvas, not a DOM probe: `measureText` is a pure read with no layout, so it is safe to call
// during render, which is where the mask geometry is built.

// One shared 2D context for every measurement. `false` marks "canvas unavailable" (SSR, a
// locked-down embed) so the fallback path is taken without retrying construction each call.
let _ctx = null;
// Memo keyed by font + tracking + string. Labels are drawn every frame of a pulse animation
// and re-measured on each render, while the set of distinct strings is tiny.
const _cache = new Map();

// A face swap changes every measurement, so drop the memo when webfonts finish loading. Any
// value cached against the fallback face was correct when taken and is stale afterwards;
// clearing is enough because the labels re-render continuously (they pulse) and will
// re-measure. Guarded for environments with no `document.fonts` (jsdom).
if (typeof document !== "undefined" && document.fonts && document.fonts.ready) {
  document.fonts.ready.then(() => _cache.clear()).catch(() => {}); // swallow-ok: cleanup
}

/**
 * Rendered width of a single line of text, in CSS px.
 *
 * `letterSpacing` is added per character rather than passed to the canvas: CSS and SVG both
 * add the tracking AFTER every glyph including the last, and `CanvasRenderingContext2D`'s own
 * `letterSpacing` is not available everywhere. Doing the arithmetic here matches what the
 * browser lays out and behaves the same in every engine.
 *
 * @param {string} text
 * @param {{fontSize: number, fontFamily: string, letterSpacing?: number}} font
 * @returns {number} width in CSS px (0 for empty text)
 */
export function measureLabelWidth(text, { fontSize, fontFamily, letterSpacing = 0 }) {
  if (!text) return 0;
  const font = `${fontSize}px ${fontFamily}`;
  const key = `${font}|${letterSpacing}|${text}`;
  const hit = _cache.get(key);
  if (hit !== undefined) return hit;
  if (_ctx === null) {
    try {
      _ctx = document.createElement("canvas").getContext("2d") || false;
    } catch {
      _ctx = false;
    }
  }
  // No canvas: fall back to the old style of estimate. Deliberately still errs WIDE (0.6em is
  // a monospace advance, and the timeline's label faces are monospace), because the failure
  // mode it protects against — dashes struck through the type — is the worse one.
  if (!_ctx) return text.length * (fontSize * 0.6 + letterSpacing);
  _ctx.font = font;
  const w = _ctx.measureText(text).width + text.length * letterSpacing;
  _cache.set(key, w);
  return w;
}
